import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import { visuallyHidden } from '@mui/utils';
import HomeIcon from '@mui/icons-material/Home';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SearchIcon from '@mui/icons-material/Search';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BarChartIcon from '@mui/icons-material/BarChart';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import PersonIcon from '@mui/icons-material/Person';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useConfiguration } from '../../hooks/useConfiguration';
import { useAuth } from '../../contexts/AuthContext';
import { useAttention } from '../../contexts/AttentionContext';
import { ROLES } from '../../config/authConfig';

interface NavigationItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
}

const mainNavigationItems: NavigationItem[] = [
  {
    id: 'home',
    label: 'Home',
    icon: <HomeIcon />,
    path: '/'
  },
  {
    id: 'search',
    label: 'Search',
    icon: <SearchIcon />,
    path: '/search'
  },
  {
    id: 'upload',
    label: 'Upload',
    icon: <UploadFileIcon />,
    path: '/upload'
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: <BarChartIcon />,
    path: '/reports'
  }
];

const platformAdminNavItems: NavigationItem[] = [
  {
    id: 'platform-admin',
    label: 'Platform Settings',
    icon: <AdminPanelSettingsIcon />,
    path: '/admin'
  }
];

// TenantAdmin lands on the exception dashboard (UX7 decision D5) — "/" redirects there, so
// "Review Queue" gets its own stable path to keep document review reachable from the sidebar.
const tenantDashboardNavItems: NavigationItem[] = [
  {
    id: 'tenant-dashboard',
    label: 'Dashboard',
    icon: <DashboardIcon />,
    path: '/dashboard'
  },
  {
    id: 'review-queue',
    label: 'Review Queue',
    icon: <AssignmentTurnedInOutlinedIcon />,
    path: '/queue'
  }
];

// TenantAdmin no longer has a separate Settings page — platform admin configures the tenant.
// Documents processed vs plan is shown in the Reports page.

/** Whichever nav item represents "the queue" for the current role carries the attention badge. */
const isQueueNavItem = (id: string): boolean => id === 'home' || id === 'review-queue';

const DRAWER_WIDTH = 240;
const COLLAPSED_WIDTH = 64;

export const SidebarNavigation: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { config } = useConfiguration();
  const { user, userRoles } = useAuth();
  const { count: attentionCount } = useAttention();
  const location = useLocation();
  const navigate = useNavigate();

  // A nav item is active when its path matches the current URL. Home ('/') must
  // match exactly (otherwise it would highlight for every route); other items
  // match their path or any nested path beneath it.
  const isItemActive = (path: string): boolean =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const applicationName = config?.ui?.applicationName || 'IDP';
  const logoUrl = config?.ui?.logoUrl;

  const isPlatformAdmin = userRoles.includes(ROLES.PLATFORM_ADMIN);
  const isTenantAdmin = userRoles.includes(ROLES.TENANT_ADMIN);

  // Role-based nav:
  // PlatformAdmin — Platform Settings only (manages tenants + platform config from there)
  // TenantAdmin — document-processing items (upload, reports, home) + Settings (own tenant)
  // Analyst — document-processing items only (upload, reports, home)
  // Dev mode — show everything so developers can test all views
  //
  // DEV MODE NOTE: isDevMode bypasses role-based nav filtering intentionally so
  // developers can reach any route without needing a specific role assigned.
  // This code path is gated on import.meta.env.DEV, which Vite replaces with
  // `false` at build time for production bundles (mode !== 'development').
  // Tree-shaking then eliminates the dead branch entirely, so this bypass
  // never ships to production — no runtime check is needed.
  const isDevMode = import.meta.env.DEV;
  const visibleMainItems = isDevMode
    ? [...tenantDashboardNavItems, ...mainNavigationItems, ...platformAdminNavItems]
    : isPlatformAdmin
      ? platformAdminNavItems
      : isTenantAdmin
        ? [...tenantDashboardNavItems, ...mainNavigationItems.filter((item) => item.id !== 'home')]
        : mainNavigationItems;

  const handleToggleDrawer = () => {
    setIsExpanded(!isExpanded);
  };

  const drawerWidth = isExpanded ? DRAWER_WIDTH : COLLAPSED_WIDTH;

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          transition: 'width 0.3s ease',
          overflowX: 'hidden',
          borderRight: 'none',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#1a237e', // GovIQ deep navy
          color: '#ffffff',
        },
      }}
    >
      {/* Header with App Name */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isExpanded ? 'space-between' : 'center',
          px: 2,
          py: 2,
          minHeight: 84,
          height: 84,
          borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
          backgroundColor: 'rgba(0, 0, 0, 0.15)',
          color: '#ffffff',
        }}
      >
        {isExpanded && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {logoUrl && (
              <img
                src={logoUrl}
                alt="Logo"
                style={{
                  height: '24px',
                  maxWidth: '32px',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  // Hide logo if it fails to load
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
            <Typography variant="h6" noWrap sx={{ fontWeight: 'bold' }}>
              {applicationName}
            </Typography>
          </Box>
        )}
        <IconButton
          onClick={handleToggleDrawer}
          size="small"
          aria-label={isExpanded ? 'Collapse navigation' : 'Expand navigation'}
          sx={{ color: 'inherit', '&:focus-visible': { outline: '2px solid rgba(255,255,255,0.9)', outlineOffset: '2px' } }}
        >
          {isExpanded ? <ChevronLeftIcon /> : <MenuIcon />}
        </IconButton>
      </Box>

      {/* Main Navigation Items */}
      <List sx={{ pt: 1, flex: 1 }}>
        {visibleMainItems.map((item) => (
          <ListItem key={item.id} disablePadding>
            <Tooltip
              title={isExpanded ? '' : item.label}
              placement="right"
              arrow
            >
              <ListItemButton
                component={NavLink}
                to={item.path}
                end={item.path === '/'}
                selected={isItemActive(item.path)}
                sx={{
                  minHeight: 48,
                  justifyContent: isExpanded ? 'initial' : 'center',
                  px: 2.5,
                  mx: 1,
                  borderRadius: 1,
                  color: 'rgba(255,255,255,0.75)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    color: '#ffffff',
                    '& .MuiListItemIcon-root': { color: '#ffffff' },
                  },
                  '&:focus-visible': {
                    outline: '2px solid rgba(255,255,255,0.9)',
                    outlineOffset: '2px',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    color: '#ffffff',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.20)',
                    },
                    '& .MuiListItemIcon-root': {
                      color: '#ffffff',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: isExpanded ? 3 : 'auto',
                    justifyContent: 'center',
                    color: 'inherit',
                  }}
                >
                  {/* The review queue nav item carries the "needs my attention" badge (held docs
                      addressed to me, UX6) — 'home' for Analysts, 'review-queue' for TenantAdmin
                      (whose "/" now lands on the exception dashboard, UX7). The numeric badge is
                      decorative for AT; the count is announced via the visually-hidden sentence
                      inside the button (see below). */}
                  {isQueueNavItem(item.id) && attentionCount > 0 ? (
                    <Badge badgeContent={attentionCount} color="error" overlap="circular" aria-hidden="true">
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  sx={{
                    opacity: isExpanded ? 1 : 0,
                    transition: 'opacity 0.3s ease',
                    '& .MuiListItemText-primary': { color: 'inherit' },
                  }}
                />
                {isQueueNavItem(item.id) && attentionCount > 0 && (
                  <Box component="span" sx={visuallyHidden}>
                    {`, ${attentionCount} document${attentionCount === 1 ? '' : 's'} need your attention`}
                  </Box>
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>

      {/* User Profile Section — click to open profile page */}
      <Box sx={{ mt: 'auto' }}>
        <Tooltip title={isExpanded ? '' : 'Profile'} placement="right" arrow>
          <Box
            onClick={() => navigate('/profile')}
            sx={{
              p: 2,
              borderTop: '1px solid rgba(255,255,255,0.12)',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.08)',
              },
              '&:focus-visible': {
                outline: '2px solid rgba(255,255,255,0.9)',
                outlineOffset: '-2px',
              },
            }}
            role="button"
            tabIndex={0}
            aria-label="Open profile"
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/profile'); } }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'primary.main',
                fontSize: '0.875rem',
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {user?.name?.charAt(0)?.toUpperCase() || <PersonIcon fontSize="small" />}
            </Avatar>
            {isExpanded && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#ffffff' }} noWrap>
                  {user?.name || 'User'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)' }} noWrap>
                  {user?.username || ''}
                </Typography>
              </Box>
            )}
          </Box>
        </Tooltip>
      </Box>
    </Drawer>
  );
};